import { useCallback, useEffect, useMemo, type FC } from 'react';
import {
  useCanLeaveNursingHomeScope,
  useCanUseTenantScope,
  useScope,
} from '@pflegenavi/frontend/nursing-home-context';
import type { ScopeKind } from '@pflegenavi/frontend/nursing-home-context';
import { useNavigate } from 'react-router-dom';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';
import { useFeatureFlag } from '@pflegenavi/frontend/feature-flags';
import { FeatureFlag, FeatureFlagStatus } from '@pflegenavi/shared/api';
import { useTranslation } from 'react-i18next';
import {
  SelectScopeView,
  type ScopeOption,
  type ScopeSecondaryPart,
} from './SelectScopeView';

const SCOPE_ICONS: Record<ScopeKind, string> = {
  nursing_home: 'mdi:home',
  legal_entity: 'mdi:domain',
  tenant: 'mdi:sitemap',
};

/**
 * Builds the secondary line of an entry, e.g. `['CSCS', 'Caritas']` for a
 * nursing home with cost center CSCS belonging to the legal entity Caritas.
 * The containing entity is emphasized while it is the selected scope, which is
 * how an entry signals that it is part of the current selection.
 */
const secondaryLine = (
  costCenter: string | null | undefined,
  containingEntityName: string | undefined,
  isIncluded: boolean
): ScopeSecondaryPart[] => {
  const parts: ScopeSecondaryPart[] = [];

  if (costCenter) {
    parts.push({ text: costCenter });
  }
  if (containingEntityName) {
    parts.push({ text: containingEntityName, emphasized: isIncluded });
  }

  return parts;
};

export const SelectScope: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    kind,
    selectedNursingHome,
    nursingHomeList,
    selectedLegalEntity,
    legalEntityList,
    knownLegalEntities,
    tenant,
    setScope,
    selectLegalEntity,
  } = useScope();

  const legalEntityScopeEnabled =
    useFeatureFlag(FeatureFlag.dev_LegalEntityScope) ===
    FeatureFlagStatus.Enabled;
  const tenantScopeEnabled =
    useFeatureFlag(FeatureFlag.dev_TenantScope) === FeatureFlagStatus.Enabled;

  const canLeaveNursingHomeScope = useCanLeaveNursingHomeScope();
  const canUseTenantScope = useCanUseTenantScope();

  const tenantName = tenant?.name ?? '';

  // Every home shows the name of the entity it belongs to, including the ones
  // the employee may not scope to, so this reads the unfiltered list.
  const legalEntityNameById = useMemo(
    () => new Map(knownLegalEntities.map((item) => [item.id, item.name])),
    [knownLegalEntities]
  );

  const tabs = useMemo(() => {
    const selectedIdFor = (tabKind: ScopeKind, id: string | undefined) =>
      kind === tabKind ? id : undefined;

    const nursingHomeIsIncluded = (legalEntityIdOfHome: string) =>
      kind === 'tenant' ||
      (kind === 'legal_entity' &&
        legalEntityIdOfHome === selectedLegalEntity?.id);

    const nursingHomeTab = {
      key: 'nursing_home' satisfies ScopeKind,
      label: t('nav.scope-selection.nursing-home'),
      avatarColor: 'primary' as const,
      searchPlaceholder: t('nav.search-nursing-homes'),
      selectedId: selectedIdFor('nursing_home', selectedNursingHome?.id),
      options: nursingHomeList.map((nursingHome) => ({
        id: nursingHome.id,
        primary: nursingHome.name,
        secondary: secondaryLine(
          nursingHome.costCenter,
          legalEntityNameById.get(nursingHome.legalEntityId),
          nursingHomeIsIncluded(nursingHome.legalEntityId)
        ),
      })),
    };

    const legalEntityTab = {
      key: 'legal_entity' satisfies ScopeKind,
      label: t('nav.scope-selection.legal-entity'),
      avatarColor: 'info' as const,
      searchPlaceholder: t('nav.scope-selection.search-legal-entities'),
      selectedId: selectedIdFor('legal_entity', selectedLegalEntity?.id),
      options: legalEntityList.map((legalEntity) => ({
        id: legalEntity.id,
        primary: legalEntity.name,
        secondary: secondaryLine(
          legalEntity.invoiceCostCenter,
          tenantName,
          kind === 'tenant'
        ),
      })),
    };

    const tenantTab = {
      key: 'tenant' satisfies ScopeKind,
      label: t('nav.scope-selection.tenant'),
      avatarColor: 'warning' as const,
      searchPlaceholder: t('nav.scope-selection.search-tenants'),
      selectedId: selectedIdFor('tenant', tenantName),
      options: [{ id: tenantName, primary: tenantName }],
    };

    return [
      nursingHomeTab,
      ...(canLeaveNursingHomeScope && legalEntityScopeEnabled
        ? [legalEntityTab]
        : []),
      ...(canUseTenantScope && tenantScopeEnabled ? [tenantTab] : []),
    ];
  }, [
    t,
    kind,
    selectedNursingHome?.id,
    nursingHomeList,
    legalEntityNameById,
    selectedLegalEntity?.id,
    legalEntityList,
    tenantName,
    canLeaveNursingHomeScope,
    canUseTenantScope,
    legalEntityScopeEnabled,
    tenantScopeEnabled,
  ]);

  // A scope whose tab is gone (role changed, feature flag switched off) would
  // otherwise stick around in client storage and filter every page. Only once
  // the roles are known though: while the profile loads both gates return
  // `undefined`, and the stored scope - read from client storage - would lose
  // the race against the network and be thrown away.
  const rolesKnown =
    canLeaveNursingHomeScope !== undefined && canUseTenantScope !== undefined;

  useEffect(() => {
    if (!rolesKnown) {
      return;
    }
    if (tabs.length && !tabs.some((tab) => tab.key === kind)) {
      setScope({ kind: 'nursing_home' });
    }
  }, [rolesKnown, tabs, kind, setScope]);

  const handleSelect = useCallback(
    (tabKey: string, option: ScopeOption) => {
      const nextKind = tabKey as ScopeKind;

      if (nextKind === 'nursing_home') {
        setScope({ kind: 'nursing_home' });
        navigate(
          nhAppMainPages.NURSING_HOME_SELECT.replace(
            ':nursingHomeId',
            option.id
          )
        );
        return;
      }

      if (nextKind === 'legal_entity') {
        const legalEntity = legalEntityList.find(
          (item) => item.id === option.id
        );
        if (legalEntity) {
          selectLegalEntity(legalEntity);
        }
      }

      setScope({ kind: nextKind });
    },
    [setScope, selectLegalEntity, legalEntityList, navigate]
  );

  const buttonLabel =
    kind === 'legal_entity'
      ? selectedLegalEntity?.name ?? ''
      : kind === 'tenant'
      ? tenantName
      : selectedNursingHome?.name ?? '';

  if (!nursingHomeList.length || !selectedNursingHome) {
    return null;
  }

  return (
    <SelectScopeView
      tabs={tabs}
      activeTabKey={kind}
      buttonLabel={buttonLabel}
      buttonIcon={SCOPE_ICONS[kind]}
      onSelect={handleSelect}
    />
  );
};
